/**
 * BHERMES Protocol Website — /protocol
 * Self-contained marketing landing page with live on-chain stats
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowRight, ExternalLink, Globe, Shield, Landmark, Layers,
  Zap, Users, Copy, Check, ChevronRight, Lock, Code,
  GitBranch, ArrowLeftRight, Eye, Wallet, Activity,
} from 'lucide-react';
import { BACKEND_URL, API } from '@/lib/backendUrl';
import GeoCitationWidget from '@/components/geo/GeoCitationWidget';


const fmt = (n) => new Intl.NumberFormat().format(Math.round(n || 0));

// ═══════════════════════════════════════════════════════
// HALVING DATA
// ═══════════════════════════════════════════════════════
const HALVING_ERAS = [
  { era: 1, hotels: '1–108',    perHotel: '1,000,000', total: '108,000,000', pctOfCap: '10%' },
  { era: 2, hotels: '109–216',  perHotel: '500,000',   total: '54,000,000',  pctOfCap: '5%' },
  { era: 3, hotels: '217–324',  perHotel: '250,000',   total: '27,000,000',  pctOfCap: '2.5%' },
  { era: 4, hotels: '325–432',  perHotel: '125,000',   total: '13,500,000',  pctOfCap: '1.25%' },
  { era: 5, hotels: '433–540',  perHotel: '62,500',    total: '6,750,000',   pctOfCap: '0.625%' },
  { era: 6, hotels: '541–648',  perHotel: '31,250',    total: '3,375,000',   pctOfCap: '0.3%' },
  { era: 7, hotels: '649+',     perHotel: '15,625',    total: '~1,687,500',  pctOfCap: '0.15%' },
];

const CHAINS = [
  { name: 'Base Sepolia', role: 'Home Chain', color: 'bg-blue-500', key: 'base_sepolia' },
  { name: 'Ethereum Sepolia', role: 'Bridge', color: 'bg-indigo-500', key: 'eth_sepolia' },
  { name: 'Arbitrum Sepolia', role: 'Bridge', color: 'bg-sky-500', key: 'arb_sepolia' },
];

// ═══════════════════════════════════════════════════════
// COPY BUTTON
// ═══════════════════════════════════════════════════════
const CopyAddr = ({ addr }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(addr);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1 text-xs mono hover:text-[#0EA5A4] transition-colors" data-testid={`copy-${addr.slice(0,8)}`}>
      {addr.slice(0, 6)}...{addr.slice(-4)}
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3 opacity-50" />}
    </button>
  );
};

// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function ProtocolWebsitePage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chains, setChains] = useState(null);
  const [govConfig, setGovConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [balRes, chainRes, govRes] = await Promise.all([
        fetch(`${API}/api/bridge/balances/0x92537BE4d02aa426F0BAB04F6300e9124f1B4Ac8`).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/bridge/chains`).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/governance/config`).then(r => r.json()).catch(() => null),
      ]);
      setStats(balRes);
      setChains(chainRes?.chains);
      setGovConfig(govRes);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-gray-100" data-testid="protocol-website">
      {/* ─── NAV ─── */}
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-xl" data-testid="protocol-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
              <span className="text-white font-bold text-sm heading-font">B</span>
            </div>
            <span className="heading-font text-lg font-semibold tracking-tight">
              <span className="text-[#F7931A]">B</span>HERMES
            </span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-gray-400">
            <a href="#stats" className="hover:text-white transition-colors">Stats</a>
            <a href="#halving" className="hover:text-white transition-colors">Halving</a>
            <a href="#chains" className="hover:text-white transition-colors">Chains</a>
            <a href="#architecture" className="hover:text-white transition-colors">Architecture</a>
            <a href="#governance" className="hover:text-white transition-colors">DAO</a>
            <a href="#developers" className="hover:text-white transition-colors">Developers</a>
          </div>
          <Button size="sm" variant="outline" className="border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/10 hover:text-[#F7931A]" onClick={() => navigate('/admin/blockchain')} data-testid="launch-app-btn">
            Launch App <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden" data-testid="protocol-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(247,147,26,0.12),transparent)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 relative">
          <div className="max-w-3xl">
            <Badge variant="outline" className="border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/5 mb-6 gap-1.5">
              <Activity className="h-3 w-3" /> Live on Base Sepolia
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl heading-font font-bold tracking-tight leading-[1.1] mb-6">
              <span className="text-[#F7931A]">B</span>HERMES
              <span className="block text-gray-400 text-2xl sm:text-3xl lg:text-4xl font-medium mt-2">
                The Blockchain Hermes Protocol
              </span>
            </h1>
            <p className="text-base md:text-lg text-gray-400 leading-relaxed max-w-2xl mb-8">
              A decentralized reputation and token economy for the global hospitality industry.
              Soulbound identity. Cross-chain liquidity. Bitcoin-backed treasury. Governed by the community.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button className="bg-[#F7931A] hover:bg-[#F7931A]/90 text-black font-medium" onClick={() => navigate('/governance')} data-testid="explore-dao-btn">
                <Landmark className="h-4 w-4 mr-1.5" /> Explore DAO
              </Button>
              <Button variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5" onClick={() => {
                document.getElementById('developers')?.scrollIntoView({ behavior: 'smooth' });
              }} data-testid="read-docs-btn">
                <Code className="h-4 w-4 mr-1.5" /> Read Docs
              </Button>
            </div>
          </div>

          {/* Floating stat pills */}
          <div className="absolute right-8 top-32 hidden xl:flex flex-col gap-3 opacity-60">
            <div className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs mono text-gray-500">
              108-hotel halving
            </div>
            <div className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs mono text-gray-500 ml-8">
              1.08B max supply
            </div>
            <div className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs mono text-gray-500">
              3 chains live
            </div>
          </div>
        </div>
      </section>

      {/* ─── LIVE STATS ─── */}
      <section id="stats" className="border-t border-white/5 bg-[#0c1020]" data-testid="protocol-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs uppercase tracking-widest text-gray-500 heading-font">Live On-Chain</span>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 bg-white/5" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Total Supply"
                value={fmt(stats?.total_across_chains || 0)}
                suffix="HERMES"
                icon={Wallet}
                testId="stat-total-supply"
              />
              <StatCard
                label="Chains"
                value={chains ? Object.keys(chains).length : 0}
                suffix="networks"
                icon={Globe}
                testId="stat-chains"
              />
              <StatCard
                label="DAO Proposals"
                value={govConfig?.proposal_count ?? 0}
                suffix="on-chain"
                icon={Landmark}
                testId="stat-proposals"
              />
              <StatCard
                label="Quorum"
                value={fmt(govConfig?.quorum_threshold || 500)}
                suffix="HERMES"
                icon={Users}
                testId="stat-quorum"
              />
            </div>
          )}

          {/* Per-chain balances */}
          {stats?.balances && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {CHAINS.map(c => {
                const b = stats.balances[c.key];
                return b ? (
                  <div key={c.key} className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`chain-balance-${c.key}`}>
                    <div className={`h-2.5 w-2.5 rounded-full ${c.color}`} />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500">{c.name}</p>
                      <p className="text-sm heading-font font-medium tabular-nums">{fmt(b.balance_formatted)} <span className="text-gray-500">HERMES</span></p>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-white/10 text-gray-500">{c.role}</Badge>
                  </div>
                ) : null;
              })}
            </div>
          )}
        </div>
      </section>

      {/* ─── HALVING TRACKER ─── */}
      <section id="halving" className="border-t border-white/5" data-testid="protocol-halving">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHeader
            icon={Layers}
            title="108-Hotel Halving Schedule"
            subtitle="Supply minted progressively as hotels register. Every 108 hotels, the reward per hotel halves — modeled after Bitcoin."
          />

          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-sm" data-testid="halving-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-white/5">
                  <th className="pb-3 pr-4">Era</th>
                  <th className="pb-3 pr-4">Hotels</th>
                  <th className="pb-3 pr-4">Per Hotel</th>
                  <th className="pb-3 pr-4">Era Total</th>
                  <th className="pb-3">% of Cap</th>
                </tr>
              </thead>
              <tbody>
                {HALVING_ERAS.map((e, i) => (
                  <tr key={i} className={`border-b border-white/[0.03] ${i === 0 ? 'text-[#F7931A]' : 'text-gray-300'}`}>
                    <td className="py-3 pr-4 heading-font font-medium">
                      {i === 0 && <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#F7931A] mr-2 animate-pulse" />}
                      Era {e.era}
                    </td>
                    <td className="py-3 pr-4 mono text-xs">{e.hotels}</td>
                    <td className="py-3 pr-4 mono text-xs">{e.perHotel}</td>
                    <td className="py-3 pr-4 mono text-xs">{e.total}</td>
                    <td className="py-3 text-xs text-gray-500">{e.pctOfCap}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-[#F7931A]" />
              Current Era
            </div>
            <span>Max Supply: <strong className="text-gray-300">1,080,000,000 HERMES</strong></span>
          </div>
        </div>
      </section>

      {/* ─── CHAIN DEPLOYMENTS ─── */}
      <section id="chains" className="border-t border-white/5 bg-[#0c1020]" data-testid="protocol-chains">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHeader
            icon={GitBranch}
            title="Chain Deployments"
            subtitle="HERMES token lives across multiple chains via LayerZero V2 OFT. Base Sepolia is the home chain where SBTs and governance reside."
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            {chains && CHAINS.map(c => {
              const chain = chains[c.key];
              if (!chain) return null;
              return (
                <Card key={c.key} className="bg-white/[0.02] border-white/5 hover:border-white/10 transition-colors" data-testid={`chain-card-${c.key}`}>
                  <CardContent className="pt-5 pb-4 px-5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`h-3 w-3 rounded-full ${c.color}`} />
                      <span className="heading-font font-medium text-sm">{chain.name}</span>
                      <Badge variant="outline" className="text-[10px] border-white/10 text-gray-500 ml-auto">{c.role}</Badge>
                    </div>
                    <div className="space-y-2.5 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">OFT Contract</span>
                        <CopyAddr addr={chain.oft_address} />
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">LZ EID</span>
                        <span className="mono text-gray-300">{chain.lz_eid}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Chain ID</span>
                        <span className="mono text-gray-300">{chain.chain_id}</span>
                      </div>
                      <Separator className="bg-white/5" />
                      <a
                        href={`${chain.explorer}/address/${chain.oft_address}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[#0EA5A4] hover:text-[#0EA5A4]/80 transition-colors"
                      >
                        View on Explorer <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Bridge visual */}
          <div className="mt-8 flex items-center justify-center gap-3 text-xs text-gray-500">
            <span className="mono">Base</span>
            <ArrowLeftRight className="h-3.5 w-3.5 text-[#0EA5A4]" />
            <span className="mono">ETH</span>
            <ArrowLeftRight className="h-3.5 w-3.5 text-[#0EA5A4]" />
            <span className="mono">Arbitrum</span>
            <ArrowLeftRight className="h-3.5 w-3.5 text-[#0EA5A4]" />
            <span className="mono">Base</span>
            <span className="ml-2 text-gray-600">Full triangle via LayerZero V2</span>
          </div>
        </div>
      </section>

      {/* ─── ARCHITECTURE ─── */}
      <section id="architecture" className="border-t border-white/5" data-testid="protocol-architecture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHeader
            icon={Shield}
            title="Hybrid-Sovereign Architecture"
            subtitle="The protocol defines rules. Platforms write data. Users own their identity."
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <ArchCard
              title="Protocol Layer"
              icon={Shield}
              color="text-[#F7931A]"
              items={[
                'Defines scoring weights & formulas',
                'Governs SBT minting & revocation',
                'Manages cross-chain token flow',
                'DAO governance for parameter changes',
              ]}
              testId="arch-protocol"
            />
            <ArchCard
              title="Platform Layer"
              icon={Globe}
              color="text-[#0EA5A4]"
              items={[
                'getbitsy.ai — first platform',
                'Writes reputation scores to SBTs',
                'Earns 5% commission on transactions',
                'Operates AI agents for hotels',
              ]}
              testId="arch-platform"
            />
            <ArchCard
              title="Identity Layer"
              icon={Lock}
              color="text-[#B08D57]"
              items={[
                'Soulbound Tokens (non-transferable)',
                'Guest, Hotel, Staff — unified SBT',
                'Scores portable across platforms',
                'Owned by individual, not platform',
              ]}
              testId="arch-identity"
            />
          </div>

          {/* Key principles */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'NFTs', desc: 'Minted by Platforms (getbitsy.ai)', icon: Layers },
              { label: 'SBTs', desc: 'Governed by Protocol (BHERMES)', icon: Lock },
              { label: 'Scores', desc: 'Written by Platforms, rules by Protocol', icon: Eye },
              { label: 'Treasury', desc: 'Bitcoin-backed, 1% protocol fee', icon: Wallet },
            ].map((p, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                <p.icon className="h-4 w-4 text-gray-500 mb-2" />
                <p className="text-sm font-medium heading-font">{p.label}</p>
                <p className="text-xs text-gray-500 mt-1">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DAO GOVERNANCE ─── */}
      <section id="governance" className="border-t border-white/5 bg-[#0c1020]" data-testid="protocol-governance">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHeader
            icon={Landmark}
            title="DAO Governance"
            subtitle="$HERMES holders govern the protocol. Create proposals, vote, and execute changes on-chain."
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
            {/* Governor stats */}
            <div className="space-y-4">
              <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5 space-y-3" data-testid="governor-stats">
                <p className="text-xs uppercase tracking-widest text-gray-500 heading-font">BHermesGovernor</p>
                {govConfig ? (
                  <div className="grid grid-cols-2 gap-3">
                    <MiniStat label="Proposals" value={govConfig.proposal_count} />
                    <MiniStat label="Quorum" value={`${fmt(govConfig.quorum_threshold)} HERMES`} />
                    <MiniStat label="Proposal Threshold" value={`${fmt(govConfig.proposal_threshold)} HERMES`} />
                    <MiniStat label="Voting Period" value={`${govConfig.voting_period_blocks} blocks`} />
                    <MiniStat label="Timelock" value={`${govConfig.timelock_delay_sec}s delay`} />
                    <MiniStat label="Current Block" value={fmt(govConfig.current_block)} />
                  </div>
                ) : <Skeleton className="h-24 bg-white/5" />}
              </div>

              <Button className="w-full bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10" onClick={() => navigate('/governance')} data-testid="go-to-governance-btn">
                Open Governance Dashboard <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            {/* DAO Roadmap */}
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5" data-testid="dao-roadmap">
              <p className="text-xs uppercase tracking-widest text-gray-500 heading-font mb-4">Governance Roadmap</p>
              <div className="space-y-4">
                {[
                  { phase: 'Testnet', trigger: 'Now', model: 'Single Owner', active: true },
                  { phase: 'Pre-Mainnet', trigger: 'Before launch', model: '3-of-5 Multi-sig', active: false },
                  { phase: 'Community', trigger: '10+ hotels', model: 'Governor DAO', active: false },
                  { phase: 'Decentralized', trigger: '50+ hotels', model: 'Pure DAO', active: false },
                ].map((r, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`h-3 w-3 rounded-full border-2 ${r.active ? 'bg-[#F7931A] border-[#F7931A]' : 'border-gray-600 bg-transparent'}`} />
                      {i < 3 && <div className="w-px h-8 bg-white/10 mt-1" />}
                    </div>
                    <div>
                      <p className={`text-sm font-medium heading-font ${r.active ? 'text-[#F7931A]' : 'text-gray-400'}`}>{r.phase}</p>
                      <p className="text-xs text-gray-500">{r.trigger} — {r.model}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── DEVELOPERS ─── */}
      <section id="developers" className="border-t border-white/5" data-testid="protocol-developers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHeader
            icon={Code}
            title="Developer Integration"
            subtitle="Platforms can integrate with BHERMES to write reputation scores and receive utility allocations."
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            {/* API endpoints */}
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5" data-testid="api-reference">
              <p className="text-xs uppercase tracking-widest text-gray-500 heading-font mb-4">API Reference</p>
              <div className="space-y-2">
                {[
                  { method: 'POST', path: '/api/reputation/mint', desc: 'Mint a new SBT' },
                  { method: 'POST', path: '/api/reputation/update-scores', desc: 'Update reputation scores' },
                  { method: 'GET', path: '/api/reputation/{wallet}', desc: 'Read SBT data' },
                  { method: 'GET', path: '/api/bridge/chains', desc: 'List supported chains' },
                  { method: 'POST', path: '/api/bridge/send', desc: 'Bridge tokens cross-chain' },
                  { method: 'GET', path: '/api/governance/config', desc: 'Governor config' },
                  { method: 'POST', path: '/api/governance/proposals', desc: 'Create proposal' },
                  { method: 'POST', path: '/api/governance/proposals/{id}/vote', desc: 'Cast vote' },
                  { method: 'POST', path: '/api/faucet/drip', desc: 'Request test tokens' },
                ].map((ep, i) => (
                  <div key={i} className="flex items-center gap-3 py-1.5 text-xs">
                    <Badge variant="outline" className={`text-[10px] w-12 justify-center ${ep.method === 'POST' ? 'border-emerald-500/30 text-emerald-400' : 'border-sky-500/30 text-sky-400'}`}>
                      {ep.method}
                    </Badge>
                    <span className="mono text-gray-300 flex-1">{ep.path}</span>
                    <span className="text-gray-500 hidden sm:inline">{ep.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Integration steps */}
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5" data-testid="integration-guide">
              <p className="text-xs uppercase tracking-widest text-gray-500 heading-font mb-4">Platform Integration</p>
              <div className="space-y-4">
                {[
                  { step: 1, title: 'Register as Platform', desc: 'Submit DAO proposal to authorize your platform address for writing reputation scores.' },
                  { step: 2, title: 'Integrate SBT Scoring', desc: 'Use the /api/reputation endpoints to mint SBTs for your users and update their scores.' },
                  { step: 3, title: 'Receive Utility Allocation', desc: 'Platforms receive a 5% utility allocation on all token transactions generated through their platform.' },
                  { step: 4, title: 'Cross-Chain Support', desc: 'Token transfers are automatic via LayerZero. SBTs remain on Base (home chain).' },
                ].map(s => (
                  <div key={s.step} className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-[#F7931A]/10 border border-[#F7931A]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs text-[#F7931A] heading-font font-medium">{s.step}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium heading-font text-gray-200">{s.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="border-t border-white/5 bg-[#080b15]" data-testid="protocol-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* LLM Citation Tracker widget */}
          <div className="mb-12" data-testid="protocol-geo-widget-wrap">
            <GeoCitationWidget />
          </div>
          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
                  <span className="text-white font-bold text-xs heading-font">B</span>
                </div>
                <span className="heading-font font-semibold">
                  <span className="text-[#F7931A]">B</span>HERMES Protocol
                </span>
              </div>
              <p className="text-xs text-gray-500 max-w-xs">
                Decentralized reputation and token economy for global hospitality. Bitcoin-backed. Community-governed.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-xs text-gray-500">
              <Link to="/governance" className="hover:text-gray-300 transition-colors">Governance</Link>
              <Link to="/faucet" className="hover:text-gray-300 transition-colors">Testnet Faucet</Link>
              <Link to="/admin/deployments" className="hover:text-gray-300 transition-colors">Deployments</Link>
              <Link to="/admin/watcher" className="hover:text-gray-300 transition-colors">Watcher</Link>
              <Link to="/metaverse/economy" className="hover:text-gray-300 transition-colors">Economy</Link>
              <Link to="/vendors" className="hover:text-gray-300 transition-colors">Vendors</Link>
              <a href="#halving" className="hover:text-gray-300 transition-colors">Halving</a>
              <a href="#chains" className="hover:text-gray-300 transition-colors">Chains</a>
              <a href="#developers" className="hover:text-gray-300 transition-colors">Developers</a>
              <a href="#architecture" className="hover:text-gray-300 transition-colors">Architecture</a>
            </div>
          </div>
          <Separator className="bg-white/5 my-8" />
          <p className="text-xs text-gray-600 text-center">
            BHERMES Protocol — Testnet. All data is on Sepolia testnets.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════

const StatCard = ({ label, value, suffix, icon: Icon, testId }) => (
  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-3.5 w-3.5 text-gray-500" />
      <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-2xl md:text-3xl heading-font font-semibold tabular-nums">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{suffix}</p>
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="max-w-2xl">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-5 w-5 text-[#F7931A]" />
      <h2 className="text-xl md:text-2xl heading-font font-semibold tracking-tight">{title}</h2>
    </div>
    <p className="text-sm text-gray-400 leading-relaxed">{subtitle}</p>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div>
    <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-sm heading-font font-medium tabular-nums mt-0.5">{value}</p>
  </div>
);

const ArchCard = ({ title, icon: Icon, color, items, testId }) => (
  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`h-5 w-5 ${color}`} />
      <h3 className="heading-font font-medium">{title}</h3>
    </div>
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
          <ChevronRight className="h-3 w-3 mt-0.5 text-gray-600 shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);
